import { FieldSets } from "client/lib/apt-merchant";
import { MerchantError } from "client/lib/apt-merchant/src/types/utils";
import { FieldErrorMessageMap } from "client/store/errorSlice";

export const BASE_ROUTE = "/app";

type BaseRoute = typeof BASE_ROUTE;

export type RouteWithoutBase =
  | `/`
  | `/logout`
  | `/agency`
  | `/cash`
  | `/check-email`
  | `/invalid-country`
  | `/lead`
  | `/maropost`
  | `/maintenance`
  | `/onboarding/bank-info`
  | `/onboarding/beneficial-owners`
  | `/onboarding/company-information`
  | `/onboarding/customer-support`
  | `/onboarding/store-details`
  | `/onboarding`
  | `/square`
  | `/squarespace`
  | `/status`
  | `/thank-you`
  | `/admin/generate-agreement`
  | `/admin/update-agreement`
  | `/wrong-email`; // TODO: implement route;

export type RouteWithBase = `${BaseRoute}${RouteWithoutBase}`;

export const routes: Record<string, RouteWithoutBase> = {
  agency: `/agency`,
  cashForm: `/cash`,
  checkEmail: `/check-email`,
  invalidCountry: `/invalid-country`,
  lead: `/lead`,
  login: `/`,
  logout: `/logout`,
  maintenance: `/maintenance`,
  onboardingBankInfo: `/onboarding/bank-info`,
  onboardingCompanyInfo: `/onboarding/company-information`,
  onboardingCustomerSupport: `/onboarding/customer-support`,
  onboardingHome: `/onboarding`,
  onboardingOwners: `/onboarding/beneficial-owners`,
  onboardingStoreDetails: `/onboarding/store-details`,
  square: `/square`,
  squarespace: `/squarespace`,
  maropost: `/maropost`,
  status: `/status`,
  thankYou: `/thank-you`,
  wrongEmail: `/wrong-email`,
  generateAgreementFile: `/admin/generate-agreement`,
  updateAgreementFile: `/admin/update-agreement`,
};

// this map is used for when we need to reference the absolute path (e.g. for testing purposes)
export const routesWithBase: Record<string, RouteWithBase> = {};

for (const [key, value] of Object.entries(routes)) {
  routesWithBase[key] = `${BASE_ROUTE}${value}` as RouteWithBase;
}

export type FormUrlParam =
  | "store-details"
  | "company-information"
  | "beneficial-owners"
  | "bank-info"
  | "customer-support";

export const isOnBusinessDetailsRoute = (pathname: string): boolean => {
  const businessRoutes: string[] = [
    routes.onboardingCompanyInfo,
    routes.onboardingOwners,
    routes.onboardingStoreDetails,
  ];
  return businessRoutes.includes(pathname);
};

const fieldSetHasErrors = (
  obj:
    | Partial<FieldSets.CompanyInformationFieldSetValueMapper<MerchantError>>
    | Partial<FieldSets.BusinessOwnerValueMapper<MerchantError>>
    | Partial<FieldSets.StoreDetailsFieldSetValueMapper<MerchantError>> = {}
) => Object.values(obj).filter((v) => v?.error).length > 0;

export const getRouteFromValidationError = (
  fieldErrorMap: FieldErrorMessageMap
): RouteWithoutBase | null => {
  if (fieldSetHasErrors(fieldErrorMap.companyInformation)) {
    return routes.onboardingCompanyInfo;
  }
  if (
    (fieldErrorMap?.owners ?? []).map(fieldSetHasErrors).filter(Boolean).length
  ) {
    return routes.onboardingOwners;
  }
  if (fieldSetHasErrors(fieldErrorMap.storeDetails)) {
    return routes.onboardingStoreDetails;
  }
  return null;
};
